import { TaskCancelledError } from "cockatiel";

import { isAbortError } from "../errors";

import { RobotoDashboardRevisionConflictException } from "./exceptions";

/**
 * Whether a failure is worth reporting to error monitoring.
 *
 * False for expected, user-resolvable outcomes that already have dedicated UI —
 * these are a normal part of the product's flow, not defects, and reporting them
 * buries real failures in noise.
 *
 * A dashboard revision conflict is the first such case: two people editing the
 * same dashboard is ordinary, the server rejecting the second save is the
 * feature working, and the user is handed a dialog to resolve it.
 *
 * Cancellations are the second: a request abandoned because the user navigated
 * away is not a failure at all. React Query filters its own cancellations before
 * calling a reporter, but two forms slip past it — an `AbortError` raised by
 * `fetch` itself, and the `TaskCancelledError` that `HttpClient`'s cockatiel
 * retry policy raises when the caller's signal aborts.
 *
 * That last one is matched with `instanceof` rather than cockatiel's own
 * `isTaskCancelledError`, which is broken upstream: it tests for
 * `'isBulkheadRejectedError' in e`, a flag `TaskCancelledError` never sets.
 */
export function shouldReportError(error: unknown): boolean {
  return !(
    error instanceof RobotoDashboardRevisionConflictException ||
    isAbortError(error) ||
    error instanceof TaskCancelledError
  );
}
